import PropTypes from 'prop-types';
import { useMediaQuery } from '@mantine/hooks';

import { Group, Title, Text, Flex, Button } from '@mantine/core';

import { PORTAL_IDS } from 'constants/portal';

import { breakpoints } from 'styles/theme.variables';

export default function Cover({
  children,
  hasBorderBottom,
  subtitle,
  title,
  customStyles,
  customChildrenStyles,
  customChildrenGroupGap,
  hidePendoBanner,
  filledButton,
  outlineButton,
}) {
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints.md})`);

  const hasActionButtons = Boolean(filledButton || outlineButton);
  const buttonSize = isDesktop ? 'md' : 'sm';

  // Forwards any remaining keys (e.g. leftSection, type, data-testid) to Mantine Button.
  // variant + size are set by Cover and intentionally override anything in config.
  const renderActionButton = (config, variant) => {
    if (!config) return null;
    const { label, ...buttonProps } = config;
    return (
      <Button {...buttonProps} variant={variant} size={buttonSize}>
        {label}
      </Button>
    );
  };

  const filledNode = renderActionButton(filledButton, 'filled');
  const outlineNode = renderActionButton(outlineButton, 'outline');

  return (
    <>
      <div id={PORTAL_IDS.coverBanner} />
      <Flex
        py={{ base: 'lg', md: 'xl' }}
        direction={{ base: 'column', md: 'row' }}
        justify="space-between"
        style={{
          ...(hasBorderBottom && { borderBottom: '1px dashed var(--border-secondary)' }),
          ...(isDesktop && { alignItems: hasActionButtons ? 'flex-start' : 'center' }),
          ...(customStyles && { ...customStyles }),
        }}
      >
        <div>
          <Title data-testid="CoverHeading" order={isDesktop ? 3 : 4}>
            {title}
          </Title>
          {subtitle && <Text mt="sm">{subtitle}</Text>}
          <div id={PORTAL_IDS.coverSubtitle} />
        </div>
        <Flex
          direction={{ md: 'row-reverse', lg: 'row' }}
          gap={isDesktop && 'md'}
          style={{
            ...(!isDesktop && (children || hasActionButtons) && {
              marginTop: 'var(--mantine-spacing-md)',
            }),
            ...(customChildrenStyles && { ...customChildrenStyles }),
          }}
        >
          <div id={PORTAL_IDS.coverBtn} />
          {hasActionButtons && (
            <Group gap="xs" wrap="nowrap">
              {isDesktop ? (
                <>
                  {outlineNode}
                  {filledNode}
                </>
              ) : (
                <>
                  {filledNode}
                  {outlineNode}
                </>
              )}
            </Group>
          )}
          {/* Dev note: children aren't always buttons (i.e. can be a menu).
          For child components, ensure the button size is sm for mobile/tablet, and md for desktop */}
          {children && <Group gap={customChildrenGroupGap || 'xs'}>{children}</Group>}
        </Flex>
      </Flex>
      {!hidePendoBanner && <Flex id="PendoBannerBottom" m="0" p="0" />}
    </>
  );
}

const actionButtonShape = PropTypes.shape({
  label: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
});

Cover.propTypes = {
  children: PropTypes.node,
  hasBorderBottom: PropTypes.bool,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  title: PropTypes.string,
  customStyles: PropTypes.shape(),
  customChildrenStyles: PropTypes.shape(),
  customChildrenGroupGap: PropTypes.oneOfType([PropTypes.string, PropTypes.shape()]),
  hidePendoBanner: PropTypes.bool,
  filledButton: actionButtonShape,
  outlineButton: actionButtonShape,
};
